import {
  Button,
  DialogActions,
  DialogContent,
  TextField,
  Dialog,
  Collapse,
  Fade,
  makeStyles,
  IconButton,
  Typography,
  MenuItem,
  Box,
  CircularProgress,
} from '@material-ui/core';
import LightTooltip from '@groove/ui/Components/LightTooltip';
import ClearIcon from '@material-ui/icons/Clear';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { intervalToDuration, formatDuration } from 'date-fns';
import tinymce from 'tinymce';
import { Position, SpinButton, ISpinButtonProps } from '@fluentui/react';
import SearchIcon from '@material-ui/icons/Search';
import { theme } from 'tailwind.config';
import { logSplunkError } from '@groove/api/analytics/splunk';
import {
  meetingRequestTemplates,
  MeetingTemplateItem,
} from '@groove/api/gateway/v1/users';
import { useQuery } from 'react-query';
import InfoIcon from '@material-ui/icons/InfoOutlined';

import MergeFieldSearchResults from './MergeFieldSearchResults';
import {
  MAX_DAYS,
  isCustomMergeField,
  isCustomMergeFieldWithDays,
  isCustomSchedulerLink,
  removeNonSFDCMergeFields,
} from './helpers';

const useStyles = makeStyles({
  dialogSize: {
    width: 520,
    maxHeight: 450,
  },
  inputRoot: {
    display: 'block',
    marginBottom: 8,
    height: 32,
    '& > [class*=MuiInputBase-root]': {
      height: 32,
    },
  },
  searchInput: {
    paddingRight: 0,
    position: 'relative',
  },
  label: {
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
  },
  topDivider: {
    marginBottom: 16,
  },
  mergeFieldContainer: {
    flexGrow: 1,
    overflowY: 'scroll',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  fadeOut: {
    left: 0,
    width: '100%',
    bottom: 0,
    position: 'sticky',
    height: '25px',
    background:
      'linear-gradient(to bottom, rgba(255,255,255,0.25) 0%,rgba(255,255,255,1) 100%)',
  },
  infoIcon: {
    fontSize: 12,
    paddingLeft: 5,
  },
});

const spinButtonStyles = {
  spinButtonWrapper: [
    {
      height: 32,
      '&::after': {
        borderColor: theme.colors['neutral/200'],
        borderRadius: 4,
      },
    },
  ],
  arrowButtonsContainer: [
    {
      width: 50,
      '& button': {
        width: '100%',
        color: theme.colors['clari-blue/500'],
      },
    },
  ],
};

export type MergeFieldGroup = {
  label: string;
  options: MergeField[];
};

export type MergeField = {
  label: string;
  name: string;
};

interface MergeFieldDialogProps {
  onClose: () => void;
  isOpen: boolean;
  onSelect?: (mergeValue: string) => void;
  excludeNonSFDCMergeFields?: boolean;
  mergeFieldProps: {
    mergeFields: MergeFieldGroup[];
    fallbackRequired?: boolean;
    addFallback?: boolean;
  };
}

type MargeFieldMeetingTypeOption = Pick<
  MeetingTemplateItem,
  'id' | 'name' | 'custom_link'
>;

const DEFAULT_LINK_TEXT = 'Book a meeting with me';

const MergeFieldDialog: FC<MergeFieldDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  excludeNonSFDCMergeFields = false,
  mergeFieldProps,
}) => {
  const { mergeFields, fallbackRequired, addFallback = true } = mergeFieldProps;
  const filteredMergeFields = excludeNonSFDCMergeFields
    ? removeNonSFDCMergeFields(mergeFields)
    : mergeFields;
  const [value, setValue] = useState('');
  const [fallbackValue, setFallbackValue] = useState('');
  const [daysToAdd, setDaysToAdd] = useState<number>(1);
  const [meetingTypes, setMeetingTypes] = useState<
    MargeFieldMeetingTypeOption[]
  >([]);
  const [selectedMeetingType, setSelectedMeetingType] = useState<string | null>(
    null,
  );
  const [customLinkLabel, setCustomLinkLabel] =
    useState<string>(DEFAULT_LINK_TEXT);

  const [selectedMergeField, setSelectedMergeField] =
    useState<MergeField | null>(null);

  const resetState = (): void => {
    setValue('');
    setFallbackValue('');
    setDaysToAdd(1);
    setSelectedMergeField(null);
  };

  const classes = useStyles({});

  const { isFetching: isMeetingTypesLoading, data: meetingTypesData } =
    useQuery(['meeting-types'], () => meetingRequestTemplates(), {
      onError: (e: Error) => {
        logSplunkError(e);
      },
    });

  const isAddDaysValid = (days?: string | number): boolean => {
    if (!selectedMergeField) return false;

    return isCustomMergeFieldWithDays(selectedMergeField)
      ? Number(days) >= 1
      : true;
  };

  const isFallbackValueValid = (): boolean => {
    let isValid = true;

    if (fallbackRequired) {
      isValid = isValid && !!fallbackValue;
    }

    return isValid && !fallbackValue.includes('"');
  };

  const showFallback =
    addFallback &&
    selectedMergeField &&
    !isCustomMergeField(selectedMergeField);

  const showNumberOfDays =
    selectedMergeField && isCustomMergeFieldWithDays(selectedMergeField);

  const customSchedulerLink =
    selectedMergeField && isCustomSchedulerLink(selectedMergeField);

  const isInsertDisabled =
    !selectedMergeField ||
    !isFallbackValueValid() ||
    !isAddDaysValid(daysToAdd);

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen]);

  const handleOnChange = useCallback(e => {
    setValue(e.target.value);
    setSelectedMergeField(null);
    setDaysToAdd(1);
  }, []);

  const handleOnChangeFallbackValue = useCallback(e => {
    setFallbackValue(e.target.value);
  }, []);

  const handleSelectMergeField = useCallback(selectedMergeField => {
    setValue(selectedMergeField.label);
    setSelectedMergeField(selectedMergeField);
  }, []);

  const handleAddDays: ISpinButtonProps['onChange'] = (
    _e,
    numberOfDays,
  ): void => {
    if (isAddDaysValid(numberOfDays)) {
      setDaysToAdd(Number(numberOfDays));
    }
  };

  const getFallbackValueErrorText = (): string => {
    if (isFallbackValueValid()) {
      return '';
    }

    if (fallbackRequired) {
      return 'The fallback value is required and cannot contain a "';
    }

    return 'The fallback value cannot contain a "';
  };

  const handleInsert = useCallback(() => {
    if (selectedMergeField) {
      let mergeValue = selectedMergeField.name;
      if (fallbackValue)
        mergeValue = `${selectedMergeField.name} || "${fallbackValue}"`;
      if (isCustomMergeFieldWithDays(selectedMergeField) && daysToAdd) {
        mergeValue = selectedMergeField.name.replace('n', `${daysToAdd}`);
      }
      if (customSchedulerLink) {
        const meetingType = meetingTypes?.find(
          type => type.id === Number(selectedMeetingType),
        );
        if (!meetingType) return;
        const href =
          meetingType.id === -1 // This means default option with default meeting link
            ? selectedMergeField.name
            : `${selectedMergeField.name}_${meetingType.custom_link}`;
        mergeValue = `
        <a href="{!${href}}" rel="noopener">${
          customLinkLabel || DEFAULT_LINK_TEXT
        }</a>
        `.trim();
      }

      const formattedMergeValue = customSchedulerLink
        ? mergeValue
        : `{!${mergeValue}}`;

      if (onSelect) {
        onSelect(formattedMergeValue);
        onClose();
        return;
      }

      tinymce.activeEditor.insertContent(formattedMergeValue);
      onClose();
      window.dispatchEvent(
        new CustomEvent('merge-field-inserted', {
          detail: {
            fallbackAdded: !!fallbackValue,
            mergeField: selectedMergeField.name,
          },
        }),
      );
    }
  }, [
    fallbackValue,
    selectedMergeField,
    onClose,
    onSelect,
    daysToAdd,
    meetingTypes,
    customLinkLabel,
    selectedMeetingType,
    customSchedulerLink,
  ]);

  useEffect(() => {
    if (
      !customSchedulerLink ||
      (meetingTypes && meetingTypes?.length > 0) ||
      !meetingTypesData
    )
      return;

    const defaultOption: MargeFieldMeetingTypeOption = {
      id: -1,
      name: 'All (Default link)',
      custom_link: '',
    };
    setSelectedMeetingType(String(defaultOption.id));

    const convertedMeetingTypesData: MargeFieldMeetingTypeOption[] =
      meetingTypesData
        ? meetingTypesData.map(({ id, name, duration, custom_link }) => {
            const durationObject = intervalToDuration({
              start: 0,
              end: duration * 1000,
            });
            const formattedDuration = formatDuration(durationObject);
            return { id, name: `${name} (${formattedDuration})`, custom_link };
          })
        : [];
    setMeetingTypes([defaultOption, ...convertedMeetingTypesData]);
  }, [meetingTypesData, meetingTypes, customSchedulerLink]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      classes={{ paper: classes.dialogSize }}
    >
      <DialogContent className={classes.dialogContent}>
        {selectedMergeField && (
          <Typography variant="caption" className={classes.label}>
            Selected field
          </Typography>
        )}
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          classes={{
            root: classes.inputRoot,
          }}
          InputProps={{
            classes: { root: classes.searchInput },
            startAdornment: !value ? <SearchIcon color="primary" /> : undefined,
            endAdornment: value ? (
              <IconButton
                style={{ maxHeight: 28 }}
                color="primary"
                onClick={resetState}
              >
                <ClearIcon />
              </IconButton>
            ) : undefined,
          }}
          placeholder="Search for a merge field"
          value={value}
          onChange={handleOnChange}
        />
        <Collapse
          in={!selectedMergeField}
          className={classes.mergeFieldContainer}
        >
          <MergeFieldSearchResults
            onSelectMergeField={handleSelectMergeField}
            mergeFields={filteredMergeFields}
            value={value}
          />
          <div className={classes.fadeOut} />
        </Collapse>
        {showFallback && (
          <Fade in={!!selectedMergeField}>
            <>
              <Typography variant="caption" className={classes.label}>
                {fallbackRequired ? 'Fallback *' : 'Fallback'}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                classes={{
                  root: classes.inputRoot,
                }}
                inputProps={{ 'data-testid': 'fallback-input' }}
                placeholder="Fallback to this value when the field is not set"
                onChange={handleOnChangeFallbackValue}
                error={!isFallbackValueValid()}
                helperText={getFallbackValueErrorText()}
              />
            </>
          </Fade>
        )}
        {showNumberOfDays && (
          <Fade in={!!selectedMergeField}>
            <>
              <Typography variant="caption" className={classes.label}>
                Number of days
                <LightTooltip
                  arrow
                  title="The dates are evaluated at time of send and may be different from what is shown"
                  placement="top"
                >
                  <InfoIcon className={classes.infoIcon} />
                </LightTooltip>
              </Typography>
              <SpinButton
                styles={spinButtonStyles}
                id={selectedMergeField.name}
                value={`+${daysToAdd}`}
                min={1}
                max={MAX_DAYS[selectedMergeField.name]}
                labelPosition={Position.top}
                ariaLabel={selectedMergeField.label}
                incrementButtonAriaLabel={`Increase ${selectedMergeField.label}`}
                decrementButtonAriaLabel={`Decrease ${selectedMergeField.label}`}
                onChange={handleAddDays}
                data-testid="number-of-days-input"
              />
            </>
          </Fade>
        )}
        {customSchedulerLink && (
          <Fade in={!!selectedMergeField}>
            <Box flexDirection="column" width="100%" gridGap={4}>
              {meetingTypes.length > 0 ? (
                <>
                  <Box mt={2}>
                    <Typography variant="caption" className={classes.label}>
                      Meeting type
                    </Typography>
                    <TextField
                      classes={{
                        root: classes.inputRoot,
                      }}
                      variant="outlined"
                      fullWidth
                      select
                      id="meeting-type-select"
                      value={selectedMeetingType}
                      onChange={e => setSelectedMeetingType(e.target.value)}
                    >
                      {meetingTypes.map(type => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box mt={2}>
                    <Typography variant="caption" className={classes.label}>
                      Customize link text
                    </Typography>
                    <TextField
                      classes={{
                        root: classes.inputRoot,
                      }}
                      id="outlined-basic"
                      value={customLinkLabel}
                      onChange={e => setCustomLinkLabel(e.target.value)}
                      variant="outlined"
                      fullWidth
                    />
                  </Box>
                </>
              ) : (
                <Box
                  display="flex"
                  width="100%"
                  height="11.5rem"
                  justifyContent="center"
                  alignItems="center"
                  gridGap="1rem"
                  data-testid="loading-meeting-types"
                >
                  <CircularProgress /> Loading options...
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          aria-label="Insert"
          disabled={isInsertDisabled || isMeetingTypesLoading}
          onClick={handleInsert}
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeFieldDialog;
